import { motion } from "framer-motion";
import {
  Car,
  Check,
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Sparkles,
} from "lucide-react";
import {
  AUTO_DETAILING,
  DETAILING_MAIN_PACKAGE,
  DETAILING_UPGRADES,
  CONTACT,
} from "@/data/site";

export default function AutoDetailing() {
  return (
    <section
      id="detailing"
      data-testid="detailing-section"
      className="relative overflow-hidden bg-[#0A192F] text-white rs-section rs-grain"
    >
      <div className="absolute inset-0 -z-0 opacity-40 pointer-events-none">
        <div className="absolute top-10 left-0 h-[420px] w-[420px] rounded-full bg-[#F97316]/15 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-[#F97316]/10 blur-[120px]" />
      </div>

      <div className="rs-container relative z-10">
        {/* Intro */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F97316]/15 border border-[#F97316]/30 px-4 py-1.5">
              <Car className="h-3.5 w-3.5 text-[#F97316]" />
              <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#F97316]">
                Mobile Car Detailing
              </span>
            </div>

            <h2 className="mt-6 font-display font-semibold text-3xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight uppercase">
              Professional Mobile Car Detailing —
              <br />
              <span className="text-[#F97316]">We Come to You.</span>
            </h2>

            <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3 max-w-xl">
              {AUTO_DETAILING.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-white/85">
                  <Check className="h-5 w-5 text-[#F97316] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="detailing-book-cta"
                className="rs-btn-primary"
              >
                Book Mobile Detailing
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="detailing-call-cta"
                className="rs-btn-ghost"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
            </div>

            <p className="mt-5 inline-flex items-center gap-2 text-sm text-white/60">
              <MapPin className="h-4 w-4 text-[#F97316]" />
              Convenient mobile detailing at your location.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-2 rounded-[2.2rem] bg-gradient-to-br from-[#F97316]/30 to-transparent blur-2xl pointer-events-none" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl aspect-[4/5]">
                <img
                  src={AUTO_DETAILING.heroImage}
                  alt="Professionally detailed car with glossy finish"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  data-testid="detailing-hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 via-transparent to-transparent" />
              </div>
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-[#F97316] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white shadow-lg">
                <MapPin className="h-3 w-3" />
                We Come To You
              </div>
              <div className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-[#0A192F]/80 backdrop-blur px-3.5 py-2 text-xs font-medium text-white/90 border border-white/10">
                <Clock className="h-3.5 w-3.5 text-[#F97316]" />
                Flexible scheduling
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main package card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 rounded-[2rem] overflow-hidden border border-[#F97316]/30 bg-gradient-to-br from-[#F97316] to-[#EA580C] p-8 md:p-12 relative"
          data-testid="detailing-main-package"
        >
          <span className="absolute top-6 right-6 inline-flex items-center rounded-full bg-[#0A192F] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="h-3 w-3 mr-1.5" />
            Main Package
          </span>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 text-white">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-white/80">
                {DETAILING_MAIN_PACKAGE.vehicleNote}
              </div>
              <h3 className="mt-3 font-display font-semibold text-2xl sm:text-3xl lg:text-4xl leading-tight uppercase">
                {DETAILING_MAIN_PACKAGE.title}
              </h3>
              <div className="mt-6 flex items-baseline gap-3">
                <div className="font-display font-bold text-6xl sm:text-7xl lg:text-8xl leading-none text-white">
                  {DETAILING_MAIN_PACKAGE.price}
                </div>
                <div className="text-sm text-white/80 pb-2">flat price</div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  data-testid="detailing-main-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-6 py-3 font-semibold hover:bg-black transition-colors"
                >
                  Book This Detail
                  <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 text-white px-6 py-3 font-semibold hover:bg-white/25 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Call to Book
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl bg-[#0A192F]/35 backdrop-blur border border-white/15 p-6">
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-white/80 mb-4">
                  What&apos;s included
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5">
                  {DETAILING_MAIN_PACKAGE.includes.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-white">
                      <Check className="h-4 w-4 flex-shrink-0 mt-0.5 text-white" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upgrades */}
        <div className="mt-14">
          <div className="max-w-2xl">
            <p className="rs-overline mb-3 !text-[#F97316]">Optional Upgrades</p>
            <h3 className="font-display font-semibold text-2xl sm:text-3xl text-white leading-tight">
              Add deeper cleans &amp; longer-lasting protection.
            </h3>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {DETAILING_UPGRADES.map((u, idx) => (
              <motion.div
                key={u.title}
                data-testid={`detailing-upgrade-${idx}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: (idx % 5) * 0.06 }}
                className="rounded-2xl bg-white/[0.05] border border-white/10 p-5 hover:bg-white/[0.08] hover:border-[#F97316]/40 transition-all"
              >
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/70">
                  Upgrade
                </div>
                <div className="mt-2 font-display font-semibold text-base text-white leading-snug">
                  {u.title}
                </div>
                <div
                  className={`mt-4 font-display font-bold ${
                    u.price === "Custom Quote"
                      ? "text-base text-white/80"
                      : "text-2xl text-[#F97316]"
                  }`}
                >
                  {u.price}
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-6 text-sm text-white/55 max-w-2xl">
            Final pricing may vary for oversized vehicles, exceptionally dirty conditions, or
            specialty finishes. We&apos;ll confirm with you before any work starts.
          </p>
        </div>
      </div>
    </section>
  );
}
